import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

// All cart mutations from these widgets (insurance, bundle, cross-sells) run
// through one shared queue: concurrent POSTs would race and the last section
// morph to land would paint a stale drawer state.
let cartMutationQueue = Promise.resolve();

class HybridCartAction extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
  }

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) || !target.matches('[data-cross-sell-variant]')) return;

    const button = this.querySelector('[data-cross-sell-add]');
    if (button instanceof HTMLButtonElement) button.disabled = target.value === '';
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const bundleButton = target.closest('[data-bundle-add]');
    if (bundleButton instanceof HTMLButtonElement) {
      event.preventDefault();
      const variantId = bundleButton.dataset.variantId;
      if (!variantId) return;

      this.#mutate({
        url: Theme.routes.cart_add_url,
        action: 'add',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        payload: { items: [{ id: Number(variantId), quantity: 1 }] },
        control: bundleButton,
      });
      return;
    }

    const crossSellButton = target.closest('[data-cross-sell-add]');
    if (crossSellButton instanceof HTMLButtonElement) {
      event.preventDefault();
      const select = this.querySelector('[data-cross-sell-variant]');
      if (!(select instanceof HTMLSelectElement) || select.value === '') {
        select?.focus();
        return;
      }

      this.#mutate({
        url: Theme.routes.cart_add_url,
        action: 'add',
        lines: [{ merchandiseId: select.value, quantity: 1 }],
        payload: { items: [{ id: Number(select.value), quantity: 1 }] },
        control: crossSellButton,
      });
      return;
    }

    const protectionInput = target.closest('[data-shipping-protection]');
    if (!(protectionInput instanceof HTMLInputElement)) return;

    const variantId = protectionInput.dataset.variantId;
    const lineKey = protectionInput.dataset.lineKey;
    if (!variantId) return;

    const isAdding = protectionInput.checked;
    this.#mutate({
      url: isAdding ? Theme.routes.cart_add_url : Theme.routes.cart_change_url,
      action: isAdding ? 'add' : 'remove',
      lines: isAdding
        ? [{ merchandiseId: variantId, quantity: 1 }]
        : [{ id: lineKey || variantId, quantity: 0 }],
      payload: isAdding
        ? { items: [{ id: Number(variantId), quantity: 1 }] }
        : { id: lineKey || Number(variantId), quantity: 0 },
      control: protectionInput,
    });
  };

  #mutate(args) {
    if (this.hasAttribute('aria-busy')) return;

    this.setAttribute('aria-busy', 'true');
    args.control.disabled = true;

    const run = () => this.#performMutation(args);
    cartMutationQueue = cartMutationQueue.then(run, run);
  }

  async #performMutation({ url, action, lines, payload, control }) {
    const status = this.querySelector('[data-cart-action-status]');
    if (status) status.textContent = '';

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const uniqueSectionIds = [...new Set(sectionIds)];
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines,
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...payload,
          sections: uniqueSectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status || result.errors) {
        throw new Error(result.description || result.message || 'Cart update failed');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          sections: result.sections,
          source: 'hybrid-cart-action',
        },
      });
    } catch (error) {
      deferred.reject(error);
      if (control instanceof HTMLInputElement) control.checked = !control.checked;
      if (status) status.textContent = error instanceof Error ? error.message : 'Cart update failed';
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Cart update failed',
          code: 'INVALID',
        })
      );
    } finally {
      // Re-enable on success too: when the sections morph does not replace
      // this node (e.g. no cart-items-component matched), the control would
      // otherwise stay disabled forever after a successful add.
      control.disabled = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('hybrid-cart-action')) {
  customElements.define('hybrid-cart-action', HybridCartAction);
}

// CRO r4: swap the cart cross-sell thumbnail to the chosen variant's photo
// (lives here, not in the snippet, because scripts inside morphed drawer HTML
// never execute).
(function () {
  if (window.__sableauCrossSellSwap) return;
  window.__sableauCrossSellSwap = true;
  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-cross-sell-variant')) return;
    var card = select.closest('.hybrid-cart__cross-sell');
    var option = select.options[select.selectedIndex];
    // Keep the shown price in sync with the chosen variant, so the Add button
    // never charges more than the card displays.
    var price = card && card.querySelector('[data-cross-sell-price]');
    if (price) {
      if (!price.dataset.defaultText) price.dataset.defaultText = price.textContent;
      var variantPrice = option ? option.getAttribute('data-price') : null;
      price.textContent = variantPrice || price.dataset.defaultText;
    }
    var image = card && card.querySelector('.hybrid-cart__cross-sell-image');
    if (!image) return;
    if (!image.dataset.defaultSrc) image.dataset.defaultSrc = image.currentSrc || image.src;
    var src = option ? option.getAttribute('data-image') : null;
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = src || image.dataset.defaultSrc;
  });
})();
